import { useCallback, useMemo } from "react";
import { useLazyQuery } from "@apollo/client";

import FlashContext from "components/FlashMessages/FlashContext";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleCellProps } from "ds/components/TableSimple/types";
import useCanDeleteVersion from "shared/Templates/access/useCanDeleteVersion";
import useTypedContext from "hooks/useTypedContext";
import { Blueprint } from "types/generated";
import TemplateFeatureGateTooltip from "views/Account/TemplatesWorkbench/FeatureGate/Tooltip";
import { TemplateActions } from "views/Account/TemplatesWorkbench/FeatureGate/types";
import { GET_TEMPLATE_VERSION } from "views/Account/TemplateVersion/gql";

import { TemplateContext } from "../../../Context";
import { showCreateVersionDrawer } from "../../CreateVersionDrawer";
import { showDeleteConfirmation } from "../../DeleteConfirmation";

const TemplatesTableRowActionsCell = ({
  item: version,
  ...cellProps
}: TableSimpleCellProps<Blueprint>) => {
  const templateContext = useTypedContext(TemplateContext);
  const { onError } = useTypedContext(FlashContext);
  const canDeleteVersionFn = useCanDeleteVersion();

  const canDeleteVersion = canDeleteVersionFn(version.space.id, version.deploymentsCount);

  const [getTemplateVersion] = useLazyQuery(GET_TEMPLATE_VERSION);

  const handleOpenCloneDrawer = useCallback(async () => {
    try {
      const { data, error } = await getTemplateVersion({
        variables: {
          id: templateContext.template.id,
          versionId: version.id,
        },
      });

      if (error) {
        onError(error);
        return;
      }

      if (data?.blueprintVersionedGroup?.blueprintVersion) {
        showCreateVersionDrawer({
          version: data.blueprintVersionedGroup.blueprintVersion,
          templateId: templateContext.template.id,
          isCloneMode: true,
        });
      }
    } catch (error) {
      onError(error);
    }
  }, [version.id, templateContext.template.id, getTemplateVersion, onError]);

  const menuItems = useMemo(() => {
    const items = [];

    items.push(
      <TemplateFeatureGateTooltip
        key="duplicate"
        on={(props) => (
          <DropdownMenuItem {...props} onAction={handleOpenCloneDrawer}>
            Duplicate
          </DropdownMenuItem>
        )}
        template={templateContext.template}
        action={TemplateActions.EditTemplate}
        tooltipPlacement="left"
      />
    );

    if (canDeleteVersion.result || canDeleteVersion.message) {
      items.push(
        <DropdownMenuItem
          key="delete"
          onAction={() =>
            showDeleteConfirmation({
              id: version.id,
              version: `${version.name} - ${version.version}`,
            })
          }
          danger
          isDisabled={!canDeleteVersion.result}
          tooltip={canDeleteVersion.message}
          tooltipPlacement="left"
        >
          Delete
        </DropdownMenuItem>
      );
    }

    return items;
  }, [version, canDeleteVersion, templateContext.template, handleOpenCloneDrawer]);

  return (
    <TableSimpleCell {...cellProps}>
      {menuItems.length > 0 && (
        <DropdownMenuEllipsis tooltip="Version actions">{menuItems}</DropdownMenuEllipsis>
      )}
    </TableSimpleCell>
  );
};

export default TemplatesTableRowActionsCell;
